import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Switch, Tooltip } from "../../common";
import AnalyticsPastDataInfoModal from "./AnalyticsPastDataInfoModal";
import {
  PointInTimeAvailability,
  isPointInTimeUsable,
  latestSelectablePointInTimeDate,
} from "../../../helpers/analytics/pointInTimeFilterHelpers";

interface AnalyticsPastDataControlProps {
  availability: PointInTimeAvailability;
  /** Client history floor; the picker will not offer anything earlier. */
  earliestSelectableDate: string | null;
  /** Data snapshot is switched on for this draft (the date may still be empty). */
  isOn: boolean;
  /** Staged / draft date, or null when no date has been chosen yet. */
  value: string | null;
  onToggleChange(isOn: boolean): void;
  onDateChange(date: string | null): void;
}

/**
 * Data snapshot control for the Advanced filters panel (ADR-0006).
 * Stays visible but disabled where point-in-time cannot be honoured, so people
 * are told why rather than left wondering where the option went.
 */
function AnalyticsPastDataControl({
  availability,
  earliestSelectableDate,
  isOn,
  value,
  onToggleChange,
  onDateChange,
}: AnalyticsPastDataControlProps) {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);

  const usable = isPointInTimeUsable(availability);
  const needsDate = usable && isOn && !value;
  const switchOn = usable && isOn;

  const unavailableReason =
    availability === "UNAVAILABLE-PAGE"
      ? t(
          "Analytics.Filters.PastDataUnavailableUsage",
          "Not available on Usage. That page is built from live activity, not stored history.",
        )
      : availability === "UNAVAILABLE-NO-HISTORY"
        ? t(
            "Analytics.Filters.PastDataUnavailableNoHistory",
            "Not available yet. Your company does not have enough stored history to look back.",
          )
        : availability === "LOADING"
          ? t(
              "Analytics.Filters.PastDataChecking",
              "Checking whether a data snapshot is available.",
            )
          : null;

  const switchRow = (
    <div className="flex items-center justify-between gap-3">
      <label
        id="analytics-filters-past-data-switch-label"
        className={`text-sm text-gray-800 ${usable ? "cursor-pointer" : "cursor-default text-gray-500"}`}
        onClick={() => {
          if (usable) onToggleChange(!isOn);
        }}
      >
        {t("Analytics.Filters.ShowPastDataToggle", "Use a past snapshot")}
      </label>
      <Switch
        checked={switchOn}
        onChange={(checked) => onToggleChange(checked)}
        isReadOnly={!usable}
        size="norm"
        ariaLabelledBy="analytics-filters-past-data-switch-label"
      />
    </div>
  );

  const unavailableTrigger = unavailableReason ? (
    <div
      tabIndex={0}
      className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
      aria-describedby="analytics-filters-past-data-unavailable"
      aria-label={unavailableReason}
    >
      {switchRow}
      <span id="analytics-filters-past-data-unavailable" className="sr-only">
        {unavailableReason}
      </span>
    </div>
  ) : null;

  const infoIconClassName =
    "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800";

  return (
    <>
      <div className="flex items-start gap-2 mb-2">
        <h4 className="min-w-0 flex-1 text-sm font-bold">
          {t("Analytics.Filters.ShowPastData", "Data snapshot")}
        </h4>
        {usable ? (
          <button
            type="button"
            className={infoIconClassName}
            aria-label={t(
              "Analytics.Filters.PastDataInfo.Open",
              "What to know about data snapshots",
            )}
            onClick={() => setInfoOpen(true)}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
          </button>
        ) : (
          unavailableReason && (
            <Tooltip
              content={unavailableReason}
              triggerElement={
                <div
                  tabIndex={0}
                  className={infoIconClassName}
                  role="img"
                  aria-label={unavailableReason}
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </div>
              }
            />
          )
        )}
      </div>

      <p className="mt-1 text-xs text-gray-500">
        {t(
          "Analytics.Filters.PastDataSubheading",
          "Choose which version of the data is used",
        )}
      </p>

      <div className="mt-3">
        {unavailableReason ? (
          <Tooltip
            content={unavailableReason}
            triggerElement={unavailableTrigger!}
          />
        ) : (
          switchRow
        )}
      </div>

      {usable && isOn && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
          <label
            className="block text-xs font-semibold text-gray-700"
            htmlFor="analytics-filters-past-data-date"
          >
            {t("Analytics.Filters.PastDataDateLabel", "Snapshot date")}
          </label>
          <input
            id="analytics-filters-past-data-date"
            type="date"
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
            value={value ?? ""}
            min={earliestSelectableDate ?? undefined}
            max={latestSelectablePointInTimeDate()}
            onChange={(e) => onDateChange(e.target.value || null)}
          />
          {needsDate ? (
            <p className="mt-1.5 text-xs text-red-600">
              {t(
                "Analytics.Filters.PastDataDateRequired",
                "Choose a date before you apply.",
              )}
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-gray-500">
              {t(
                "Analytics.Filters.PastDataDateHelper",
                "Shows the data held at the end of the selected date (UTC). Other date filters already set above will still apply.",
              )}
            </p>
          )}
        </div>
      )}

      <AnalyticsPastDataInfoModal
        isOpen={infoOpen}
        onOpenChange={setInfoOpen}
        isAboveOverlay
      />
    </>
  );
}

export default AnalyticsPastDataControl;
